/**
 * The email an invited person gets when a trip is finalised.
 *
 * Separate from the owner's own confirmation in App.context so the two can say
 * different things — the owner is being told "you finalised it", the guest is
 * being told "you are going on this". Both go through the same `mail`
 * collection and the same Trigger Email extension.
 *
 * THE LINK IS BUILT FROM THE RUNNING ORIGIN.
 * The owner's confirmation email hardcodes http://localhost:3000 and shipped
 * that way, so its only call to action is dead for every real recipient. That
 * mistake is not repeated here; `origin` is a parameter so this stays testable
 * without a DOM.
 */

import { inviteLink } from './tripSharing';

const BRAND_GRADIENT = 'linear-gradient(135deg, #FD4F68 0%, #FF7656 100%)';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatDate(millis) {
  if (!Number.isFinite(millis)) return '';
  return new Date(millis).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatRange(dates) {
  const list = (dates || []).filter(Number.isFinite);
  if (!list.length) return 'Dates to be confirmed';
  const start = formatDate(list[0]);
  const end = formatDate(list[list.length - 1]);
  return start === end ? start : `${start} – ${end}`;
}

/** Subject line for the guest copy of a finalised itinerary. */
export const SHARED_ITINERARY_SUBJECT = 'Your Vhakacha trip is confirmed 🎉';

/** Subject line for the guest copy of a booking. */
export const SHARED_BOOKING_SUBJECT = 'Your Vhakacha booking details';

/**
 * @param {object} snapshot  the published trip snapshot
 * @param {object} invite    the person this copy is for
 * @param {string} organiser how to name the person who planned it
 * @param {string} [origin]  overrides window.location.origin, for tests
 */
export function buildSharedItineraryEmailHtml({ snapshot, invite, organiser, origin }) {
  const name = escapeHtml(invite?.name || 'there');
  const title = escapeHtml(snapshot?.title || 'Your trip');
  const location = escapeHtml(snapshot?.location || 'your destination');
  const dates = escapeHtml(formatRange(snapshot?.dates));
  const stay = escapeHtml(snapshot?.accommodation?.name || 'To be confirmed');
  const who = escapeHtml(organiser || 'The trip organiser');
  const link = escapeHtml(inviteLink(invite?.inviteId, origin));

  const days = (snapshot?.schedule || [])
    .map((day) => {
      const slots = (day.slots || [])
        .map(
          (slot) =>
            `<li style="margin-bottom:6px;font-size:14px;color:#4a4a4a;">
               <strong>${escapeHtml(slot.dayTime)}</strong>
               <span style="color:#888;">${escapeHtml(slot.time)}</span>
             </li>`
        )
        .join('');
      return `
        <div style="margin-bottom:16px;">
          <p style="margin:0 0 6px 0;font-weight:700;color:#282C34;font-size:15px;">Day ${escapeHtml(day.day)}</p>
          <ul style="margin:0;padding-left:18px;">${slots}</ul>
        </div>`;
    })
    .join('');

  return `
    <div style="font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;color:#4a4a4a;max-width:650px;margin:0 auto;border-radius:12px;overflow:hidden;box-shadow:0 4px 15px rgba(0,0,0,0.1);border:1px solid #eaeaea;">
      <div style="background:${BRAND_GRADIENT};color:#fff;padding:30px 20px;text-align:center;">
        <h1 style="margin:0;font-size:26px;font-weight:700;">You're going to ${location} ✈️</h1>
        <p style="margin:10px 0 0 0;font-size:16px;opacity:0.9;">${who} has finalised the plans.</p>
      </div>

      <div style="padding:36px 30px;background:#fff;">
        <p style="font-size:16px;line-height:1.6;margin-top:0;">Hi ${name},</p>
        <p style="font-size:16px;line-height:1.6;">
          Here is the final copy of <strong>${title}</strong>. Nothing more is needed from you —
          this is your record of the trip.
        </p>

        <div style="background:#f8f9fa;border-left:4px solid #FF7656;padding:20px;border-radius:0 8px 8px 0;margin:25px 0;">
          <h2 style="margin:0 0 15px 0;color:#333;font-size:18px;">Trip highlights</h2>
          <table style="width:100%;border-collapse:collapse;">
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">📍</td><td style="padding:8px 0;font-size:15px;"><strong>Where:</strong> ${location}</td></tr>
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">📅</td><td style="padding:8px 0;font-size:15px;"><strong>When:</strong> ${dates}</td></tr>
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">🏨</td><td style="padding:8px 0;font-size:15px;"><strong>Staying at:</strong> ${stay}</td></tr>
          </table>
        </div>

        ${days ? `<h2 style="color:#333;font-size:18px;margin-bottom:12px;">Your day plan</h2>${days}` : ''}

        <div style="text-align:center;margin:36px 0;">
          <a href="${link}" style="display:inline-block;background:${BRAND_GRADIENT};color:#fff;padding:14px 30px;text-decoration:none;border-radius:30px;font-weight:bold;font-size:15px;letter-spacing:0.5px;">
            View the full trip
          </a>
        </div>

        <p style="font-size:14px;line-height:1.6;color:#666;margin-bottom:0;">
          This link is yours. Please do not forward it — ${who} can remove it at any time.
        </p>
      </div>

      <div style="background:#f1f1f1;padding:20px;text-align:center;font-size:13px;color:#888;">
        <p style="margin:0;">Happy travels 🌍</p>
        <p style="margin:5px 0 0 0;"><strong>The Vhakacha Team</strong></p>
      </div>
    </div>
  `;
}

/**
 * The booking equivalent, sent when the organiser circulates the details.
 *
 * Money is included because the people on a shared booking are usually the
 * people splitting it. The platform's commission is not, for the same reason
 * it appears nowhere in the traveller app.
 */
export function buildSharedBookingEmailHtml({ snapshot, invite, organiser, origin }) {
  const name = escapeHtml(invite?.name || 'there');
  const title = escapeHtml(snapshot?.title || 'Your booking');
  const location = escapeHtml(snapshot?.location || '');
  const dates = escapeHtml(formatRange(snapshot?.dates));
  const who = escapeHtml(organiser || 'The trip organiser');
  const link = escapeHtml(inviteLink(invite?.inviteId, origin));
  const status = escapeHtml(snapshot?.status || 'PENDING');

  let total = '';
  try {
    total = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: snapshot?.currency || 'USD',
    }).format(Number(snapshot?.totalPaid) || 0);
  } catch {
    total = `${snapshot?.currency || 'USD'} ${(Number(snapshot?.totalPaid) || 0).toFixed(2)}`;
  }

  return `
    <div style="font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;color:#4a4a4a;max-width:650px;margin:0 auto;border-radius:12px;overflow:hidden;box-shadow:0 4px 15px rgba(0,0,0,0.1);border:1px solid #eaeaea;">
      <div style="background:${BRAND_GRADIENT};color:#fff;padding:30px 20px;text-align:center;">
        <h1 style="margin:0;font-size:26px;font-weight:700;">${title}</h1>
        <p style="margin:10px 0 0 0;font-size:16px;opacity:0.9;">${who} has shared these booking details with you.</p>
      </div>

      <div style="padding:36px 30px;background:#fff;">
        <p style="font-size:16px;line-height:1.6;margin-top:0;">Hi ${name},</p>

        <div style="background:#f8f9fa;border-left:4px solid #FF7656;padding:20px;border-radius:0 8px 8px 0;margin:25px 0;">
          <table style="width:100%;border-collapse:collapse;">
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">📍</td><td style="padding:8px 0;font-size:15px;"><strong>Where:</strong> ${location}</td></tr>
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">📅</td><td style="padding:8px 0;font-size:15px;"><strong>When:</strong> ${dates}</td></tr>
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">💳</td><td style="padding:8px 0;font-size:15px;"><strong>Total:</strong> ${escapeHtml(total)}</td></tr>
            <tr><td style="padding:8px 0;font-size:20px;width:30px;">🎫</td><td style="padding:8px 0;font-size:15px;"><strong>Status:</strong> ${status}</td></tr>
          </table>
        </div>

        <div style="text-align:center;margin:36px 0;">
          <a href="${link}" style="display:inline-block;background:${BRAND_GRADIENT};color:#fff;padding:14px 30px;text-decoration:none;border-radius:30px;font-weight:bold;font-size:15px;letter-spacing:0.5px;">
            View the booking
          </a>
        </div>

        <p style="font-size:14px;line-height:1.6;color:#666;margin-bottom:0;">
          This link is yours. Please do not forward it — ${who} can remove it at any time.
        </p>
      </div>

      <div style="background:#f1f1f1;padding:20px;text-align:center;font-size:13px;color:#888;">
        <p style="margin:0;">Happy travels 🌍</p>
        <p style="margin:5px 0 0 0;"><strong>The Vhakacha Team</strong></p>
      </div>
    </div>
  `;
}
